import { Swords, Shield, Sparkles } from "lucide-react";

/**
 * Combat Log — a tiny "last N rounds" ticker that lives beneath the CombatPanel.
 * Rendered only while a fight is active. Owned by Dashboard which pushes new
 * entries into an array when SSE `done` events arrive with combat data.
 *
 * Each entry: {round, action, dealt, taken, crit, enemy_hp_after, enemy_max, dead}
 */
export default function CombatLog({ entries = [] }) {
  if (!entries || entries.length === 0) return null;

  return (
    <div
      className="panel p-3 space-y-1.5"
      data-testid="combat-log"
      style={{ background: "linear-gradient(180deg, rgba(20,10,6,0.7), rgba(8,4,2,0.9))" }}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[9px] tracking-[0.4em] ash-text">— COMBAT LOG —</span>
      </div>
      <div className="max-h-32 overflow-y-auto pr-1 space-y-1" style={{ scrollbarWidth: "thin" }}>
        {entries.slice(-3).reverse().map((e, i) => (
          <div
            key={`log-${e.round}-${i}`}
            className={`flex items-center gap-2 text-[11px] font-narrative px-2 py-1 rounded-sm ${
              i === 0 ? "bg-white/[0.04] border-l-2 border-[#d4af37]/40" : "opacity-70"
            }`}
            data-testid={`combat-log-entry-${e.round}`}
          >
            <span className="text-[9px] tracking-widest ash-text w-8 flex-shrink-0">R{e.round}</span>
            <span className="flex items-center gap-1 flex-shrink-0" style={{ minWidth: 90 }}>
              {e.action === "guard" ? (
                <><Shield size={11} className="text-[#89b4c4]" /><span className="text-[#89b4c4] text-[10px] tracking-widest">GUARD</span></>
              ) : e.action === "flee" ? (
                <><span className="text-[10px] tracking-widest ash-text">FLEE</span></>
              ) : e.crit ? (
                <><Sparkles size={11} className="text-[#fff2b0]" /><span className="text-[10px] tracking-widest" style={{ color: "#fff2b0", textShadow: "0 0 8px rgba(255,242,176,0.7)" }}>CRIT!</span></>
              ) : (
                <><Swords size={11} className="blood-text" /><span className="blood-text text-[10px] tracking-widest">STRIKE</span></>
              )}
            </span>
            <span className="flex-1 truncate">
              {e.action === "flee" ? (
                <>
                  <span className="ash-text">retreated</span>
                  {e.taken > 0 && <> · <span className="blood-text">-{e.taken} HP</span></>}
                </>
              ) : e.dead ? (
                <>
                  <span className={e.crit ? "gold-text" : "blood-text"}>-{e.dealt}</span>
                  <span className="ash-text mx-1">·</span>
                  <span className="gold-text font-heading tracking-widest">KILL</span>
                </>
              ) : (
                <>
                  <span className={e.crit ? "gold-text" : "blood-text"}>-{e.dealt}</span>
                  {e.enemy_max > 0 && (
                    <span className="ash-text ml-1">
                      ({e.enemy_hp_after}/{e.enemy_max})
                    </span>
                  )}
                  {e.taken > 0 && (
                    <>
                      <span className="ash-text mx-1">·</span>
                      <span className="text-[#ff6b3a]">-{e.taken} HP</span>
                    </>
                  )}
                </>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
